<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    default: null,
  },
  contact: {
    type: Object,
    default: null,
  },
  contactOnly: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

// 从 conversation metadata 中获取渠道信息
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);

const currentConversationMetaData = computed(() => {
  if (!props.conversationId) return {};
  return conversationMetadataGetter.value(props.conversationId) || {};
});

// 从 conversation 的 custom_attributes 中获取渠道信息
const conversationChannelInfo = computed(() => {
  const customAttributes =
    currentConversationMetaData.value?.custom_attributes || {};
  return customAttributes.channel_info || {};
});

// 从 contact 的 additional_attributes 中获取渠道信息
// 注意：contact store 使用 camelcaseKeys deep 转换，所以字段名可能是 camelCase 或 snake_case
const contactChannelInfo = computed(() => {
  const attrs = props.contact?.additional_attributes || props.contact?.additionalAttributes || {};
  const info = attrs.channel_info || attrs.channelInfo || {};
  // channel_info 内部的值也可能被转换，确保取到原始 key
  if (Object.keys(info).length === 0) {
    // 遍历 additional_attributes 寻找可能的 channel_info 键
    for (const key of Object.keys(attrs)) {
      if (key.toLowerCase() === 'channel_info' || key.toLowerCase() === 'channelinfo') {
        return attrs[key] || {};
      }
    }
  }
  return info;
});

// 格式化显示键值对
const formatEntries = info => {
  return Object.entries(info).map(([key, value]) => {
    return { key, value };
  });
};

const conversationEntries = computed(() =>
  formatEntries(conversationChannelInfo.value)
);

const contactEntries = computed(() =>
  formatEntries(contactChannelInfo.value)
);

// 检查是否有渠道信息
const hasConversationChannelInfo = computed(() => {
  return Object.keys(conversationChannelInfo.value).length > 0;
});

const hasContactChannelInfo = computed(() => {
  return Object.keys(contactChannelInfo.value).length > 0;
});

// 格式化显示值
const formatDisplayValue = (key, value) => {
  if (!value) return '-';

  // 特殊处理某些键
  if (key === 'referral' || key === 'utm_source') {
    return value || '(direct)';
  }

  return String(value);
};
</script>

<template>
  <div
    class="flex flex-col gap-4"
    :class="contactOnly
      ? 'rounded-xl bg-n-solid-1 p-4 w-full'
      : 'p-4'"
  >
    <!-- 对话渠道信息 -->
    <div v-if="conversationId">
      <h3 class="text-sm font-medium text-n-slate-12 mb-3">
        {{ t('CONVERSATION_SIDEBAR.CHANNEL_INFO_TITLE') }}
      </h3>
      <div
        v-if="hasConversationChannelInfo"
        class="grid grid-cols-2 gap-2 text-sm"
      >
        <div
          v-for="entry in conversationEntries"
          :key="entry.key"
          class="flex flex-col"
        >
          <span class="text-n-slate-10 text-xs">
            {{ entry.key }}
          </span>
          <span class="text-n-slate-12 text-sm font-medium break-all">
            {{ formatDisplayValue(entry.key, entry.value) }}
          </span>
        </div>
      </div>
      <div v-else class="text-sm text-n-slate-11 text-center py-2">
        {{ t('CONVERSATION_SIDEBAR.NO_CONVERSATION_CHANNEL_INFO') }}
      </div>
    </div>

    <!-- 分隔线 -->
    <div v-if="conversationId" class="border-t border-n-weak" />

    <!-- 联系人渠道信息 -->
    <div>
      <h3 class="text-sm font-medium text-n-slate-12 mb-3">
        {{ t('CONVERSATION_SIDEBAR.CONTACT_CHANNEL_INFO_TITLE') }}
      </h3>
      <div v-if="hasContactChannelInfo" class="grid grid-cols-2 gap-2 text-sm">
        <div
          v-for="entry in contactEntries"
          :key="entry.key"
          class="flex flex-col"
        >
          <span class="text-n-slate-10 text-xs">
            {{ entry.key }}
          </span>
          <span class="text-n-slate-12 text-sm font-medium break-all">
            {{ formatDisplayValue(entry.key, entry.value) }}
          </span>
        </div>
      </div>
      <div v-else class="text-sm text-n-slate-11 text-center py-2">
        {{ t('CONVERSATION_SIDEBAR.NO_CONTACT_CHANNEL_INFO') }}
      </div>
    </div>
  </div>
</template>
